import { GAME_CONFIG } from '../config';

export interface AABB {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CollisionResult {
  collided: boolean;
  pushX: number;
  pushY: number;
  onGround: boolean;
  onCeiling: boolean;
  onLeftWall: boolean;
  onRightWall: boolean;
}

/**
 * Tile-based collision detection with AABB resolution
 */
export class CollisionGrid {
  private grid: number[][] = [];
  private width: number = 0;
  private height: number = 0;
  private tileSize: number;

  constructor() {
    this.tileSize = GAME_CONFIG.TILE_SIZE;
  }

  /**
   * Load collision grid from level data
   * @param data 2D array where 1 = solid, 0 = empty
   */
  loadGrid(data: number[][]): void {
    this.grid = data;
    this.height = data.length;
    this.width = data.length > 0 ? data[0].length : 0;
  }

  /**
   * Check if a tile position is solid
   */
  isSolid(tileX: number, tileY: number): boolean {
    if (tileX < 0 || tileX >= this.width || tileY < 0 || tileY >= this.height) {
      return true; // Out of bounds = solid
    }
    return this.grid[tileY][tileX] === 1;
  }

  /**
   * Check if a world position is inside a solid tile
   */
  isSolidAtWorld(worldX: number, worldY: number): boolean {
    const tileX = Math.floor(worldX / this.tileSize);
    const tileY = Math.floor(worldY / this.tileSize);
    return this.isSolid(tileX, tileY);
  }

  /**
   * Resolve AABB collision against the tile grid
   * Returns push vector to resolve collision
   */
  resolveCollision(box: AABB, _velocityX: number, _velocityY: number): CollisionResult {
    const result: CollisionResult = {
      collided: false,
      pushX: 0,
      pushY: 0,
      onGround: false,
      onCeiling: false,
      onLeftWall: false,
      onRightWall: false,
    };

    // Get tile bounds that the box overlaps
    const leftTile = Math.floor(box.x / this.tileSize);
    const rightTile = Math.floor((box.x + box.width - 0.01) / this.tileSize);
    const topTile = Math.floor(box.y / this.tileSize);
    const bottomTile = Math.floor((box.y + box.height - 0.01) / this.tileSize);

    // Check each overlapped tile
    for (let ty = topTile; ty <= bottomTile; ty++) {
      for (let tx = leftTile; tx <= rightTile; tx++) {
        if (!this.isSolid(tx, ty)) continue;

        // Calculate tile AABB
        const tileBox: AABB = {
          x: tx * this.tileSize,
          y: ty * this.tileSize,
          width: this.tileSize,
          height: this.tileSize,
        };

        // Check AABB overlap
        if (!this.aabbOverlap(box, tileBox)) continue;

        result.collided = true;

        // Calculate overlap on each axis
        const overlapX = this.getOverlapX(box, tileBox);
        const overlapY = this.getOverlapY(box, tileBox);

        // Resolve based on smallest overlap (separating axis)
        if (Math.abs(overlapX) < Math.abs(overlapY)) {
          // Horizontal collision
          result.pushX += overlapX;
          if (overlapX > 0) {
            result.onLeftWall = true;
          } else {
            result.onRightWall = true;
          }
        } else {
          // Vertical collision
          result.pushY += overlapY;
          if (overlapY > 0) {
            result.onCeiling = true;
          } else {
            result.onGround = true;
          }
        }
      }
    }

    return result;
  }

  private aabbOverlap(a: AABB, b: AABB): boolean {
    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }

  private getOverlapX(a: AABB, b: AABB): number {
    const aCenter = a.x + a.width / 2;
    const bCenter = b.x + b.width / 2;
    const halfWidths = a.width / 2 + b.width / 2;
    const dx = aCenter - bCenter;

    if (dx > 0) {
      // a is to the right of b, push right
      return halfWidths - dx;
    } else {
      // a is to the left of b, push left
      return -(halfWidths + dx);
    }
  }

  private getOverlapY(a: AABB, b: AABB): number {
    const aCenter = a.y + a.height / 2;
    const bCenter = b.y + b.height / 2;
    const halfHeights = a.height / 2 + b.height / 2;
    const dy = aCenter - bCenter;

    if (dy > 0) {
      // a is below b, push down
      return halfHeights - dy;
    } else {
      // a is above b, push up
      return -(halfHeights + dy);
    }
  }

  /**
   * Get grid dimensions
   */
  getDimensions(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }
}
